import React, { useCallback, useMemo } from "react";
import {
  Box,
  Button,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
  EntitySelectorTrigger,
  Icon,
  useEntitySelector,
  type EntityItemConfig,
  type EntitySelectorConfig,
} from "@salestrak/ui";
import { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { useUserPool, USER_POOL_ID } from "#app/components/entity-selectors/hooks";
import { selectionToUserEntity } from "#app/components/entity-selectors/utils";
import {
  assigneeToSelection,
  useAssigneeSelection,
} from "#app/components/entity-selectors/use-assignee-selection";
import { EntityGroupTag } from "#app/components/entity";
import { canManageSharing, type AccessLevel } from "#app/lib/utils/access-level";
import type { EntityRefValue } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = USER_POOL_ID;

// ---------------------------------------------------------------------------
// Compact trigger — avatar group styled as a ghost button
// ---------------------------------------------------------------------------

function AssigneeSelectorTrigger() {
  const { selections, open } = useEntitySelector();

  // Freeze displayed content while popover is open so the trigger
  // doesn't resize and shift the popover anchor.
  const snapshotRef = React.useRef(selections);
  if (!open) {
    snapshotRef.current = selections;
  }
  const displayedSelections = snapshotRef.current;

  // All assignees (not just the primary avatar) so the hover can list them all.
  const hoverEntities = React.useMemo(
    () => displayedSelections.map(selectionToUserEntity),
    [displayedSelections],
  );

  return (
    // `asChild` is `render` on the particle trigger.
    <EntitySelectorTrigger
      render={
        // `xs`, not `icon-xs`: an `icon-*` size is a fixed square, and a
        // stacked avatar group is wider than one — two 20px slots overlapped
        // measure ~36px inside a 24px box, so it overflows both edges and the
        // right one lands outside the card's padding. `xs` hugs its content and
        // matches the age/notes buttons at the other end of the row.
        <Button variant="ghost" size="xs">
          {hoverEntities.length > 0 ? (
            // Stacked avatar group with capped count chip; the trigger button
            // owns the press, so the group is inert.
            <EntityGroupTag
              entities={hoverEntities}
              size="xs"
              title="Assignees"
              interactive={false}
              hoverDisabled={open}
              // Card real estate is too dense for live dots — presence still
              // shows in the hover list card and everywhere else.
              presence={false}
            />
          ) : (
            <Box width={20} height={20} align="center" justify="center">
              <Icon name="user-plus" size="md" color="muted" />
            </Box>
          )}
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type IssueCardAssigneeSelectorProps = Omittable<{
  issueId: string;
  assignees: EntityRefValue[];
  onViewProfile: (userId: string) => void;
  // Viewer's access level on this issue — drives whether the grant-access
  // prompt offers the share actions (full_edit) or info-only copy.
  viewerAccessLevel?: AccessLevel | null;
}>;

export const IssueCardAssigneeSelector = React.memo(function IssueCardAssigneeSelector({
  issueId,
  assignees,
  onViewProfile,
  viewerAccessLevel,
}: IssueCardAssigneeSelectorProps) {
  const { updateAssignees } = useIssueViewMutations();
  const { pool } = useUserPool();
  const viewerCanManageSharing = canManageSharing(viewerAccessLevel);

  const initialSelections = useMemo(() => {
    // Build a map for fast pool lookups
    const poolMap = new Map(pool.items.map((item) => [item.id, item]));

    return assignees.filter((a) => a.user || poolMap.has(a.entityId)).map((a) => assigneeToSelection(a, poolMap));
  }, [assignees, pool.items]);

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [
        {
          ...pool,
          itemBadge: { label: "Profile" },
        },
      ],
      searchPlaceholder: "Search members...",
      emptyMessage: "No members found",
    }),
    [pool],
  );

  const {
    draftSelections,
    handleValueChange,
    handleOpenChange,
    getSetPrimaryLabel,
    handleSetPrimary,
  } = useAssigneeSelection({
    initialSelections,
    issue: { id: issueId, issueId },
    updateAssignees,
    viewerCanManageSharing,
  });

  const getProfileActionLabel = useCallback(() => "Profile", []);

  const handleViewProfile = useCallback(
    (entity: EntityItemConfig) => {
      onViewProfile(entity.id);
    },
    [onViewProfile],
  );

  return (
    // Stop the click bubbling so the card behind never sees it.
    // shrink={false}: the avatar cluster is the row's fixed end. Letting it
    // shrink below its content and clipping the overflow slices the avatar.
    <Box
      shrink={false}
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
      <EntitySelector
          config={config}
          value={draftSelections}
          onValueChange={handleValueChange}
          onOpenChange={handleOpenChange}
        >
          <AssigneeSelectorTrigger />
          <EntitySelectorContent size="sm">
            <EntitySelectorInput />
            <EntitySelectorSelected
              label="Assignees"
              hideWhenEmpty
              hideBadge
              hideSelectionHighlight
              maxHeight={160}
              getItemActionLabel={getProfileActionLabel}
              onItemAction={handleViewProfile}
              getItemSecondaryActionLabel={getSetPrimaryLabel}
              onItemSecondaryAction={handleSetPrimary}
            />
            <EntitySelectorPool value={POOL_ID} hideSelectionHighlight onItemAction={handleViewProfile} />
            <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
          </EntitySelectorContent>
        </EntitySelector>
    </Box>
  );
});
